import { useCallback } from 'react'

import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'

import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

import {
  automationDescriptionAtom,
  automationNameAtom,
  lastSavedPresentationAtom,
  lastSavedNameAtom,
  lastSavedDescriptionAtom,
  hasMetadataChangedAtom,
  hasPresentationChangedAtom,
  presentationAtom,
  useResolvedNodeDefinitions,
  editingVersionAtom,
  editingVersionIdAtom
} from 'Automations/automationEditor/atoms'
import {
  saveNewAutomation,
  saveAutomationVersion
} from 'Automations/automationEditor/utils'
import { presentationToApiPresentation } from 'Automations/nodeDefinitions'
import { automationsPaths } from 'Automations/router'
import { useAutomationId } from '../useAutomationId'
import { useCurrentUser } from 'common/hooks'

type SavedVersion = {
  automationId: string
  versionId: string
}

/**
 * Saves the current automation version, returning the saved ids. Throws on
 * failure (including pre-flight transform errors on an invalid design) so the
 * calling save/publish flow can surface the error notification.
 */
type UseSaveAutomationVersion = () => (args: {
  changelog: string | undefined
}) => Promise<SavedVersion>

export const useSaveAutomationVersion: UseSaveAutomationVersion = () => {
  const navigate = useNavigate()
  const { isNewAutomation, automationId } = useAutomationId()

  const name = useAtomValue(automationNameAtom)
  const description = useAtomValue(automationDescriptionAtom)
  const presentation = useAtomValue(presentationAtom)
  const hasMetadataChanged = useAtomValue(hasMetadataChangedAtom)
  const hasPresentationChanged = useAtomValue(hasPresentationChangedAtom)
  const editingVersionId = useAtomValue(editingVersionIdAtom)
  const definitions = useResolvedNodeDefinitions()

  const { email } = useCurrentUser()

  const setLastSavedPresentation = useSetAtom(lastSavedPresentationAtom)
  const setLastSavedName = useSetAtom(lastSavedNameAtom)
  const setLastSavedDescription = useSetAtom(lastSavedDescriptionAtom)
  const setEditingVersion = useSetAtom(editingVersionAtom)

  return useCallback(
    async ({
      changelog
    }: {
      changelog: string | undefined
    }): Promise<SavedVersion> => {
      if (!presentation) throw new Error('presentation is undefined')

      if (isNewAutomation) {
        // create a new automation, which also saves first version
        const newAutomationId = await saveNewAutomation({
          name,
          description,
          presentation,
          definitions
        })

        // get the ID of that first version
        const resGetVersions =
          await dashboardv99990101Client.getAutomationVersions({
            automationId: newAutomationId
          })

        if (!resGetVersions.data || resGetVersions.error) {
          throw new Error('Unable to get automation versions')
        }

        const version = resGetVersions.data.items.at(0)

        if (!version) {
          throw new Error('Unable to find an automation version')
        }

        setLastSavedPresentation(presentationToApiPresentation(presentation))
        setLastSavedName(name)
        setLastSavedDescription(description)
        setEditingVersion({
          ...version,
          createdByEmail: version.createdByEmail ?? email
        })

        navigate(
          automationsPaths.automationsEditor({ automationId: newAutomationId }),
          { replace: true }
        )

        return { automationId: newAutomationId, versionId: version.id }
      }

      if (!automationId) throw new Error('automationId is undefined')

      // Only PATCH if name/description changed
      if (hasMetadataChanged) {
        await dashboardv99990101Client.patchAutomation({
          id: automationId,
          body: { name, description }
        })
        setLastSavedName(name)
        setLastSavedDescription(description)
      }

      // Only create a new version if presentation changed
      if (hasPresentationChanged) {
        const newVersion = await saveAutomationVersion({
          automationId,
          name,
          description,
          presentation,
          definitions,
          changelog: changelog ?? ''
        })

        setLastSavedPresentation(presentationToApiPresentation(presentation))
        setEditingVersion({
          ...newVersion,
          createdByEmail: newVersion.createdByEmail ?? email
        })

        return { automationId, versionId: newVersion.id }
      }

      // Metadata-only save — return existing version ID
      if (!editingVersionId) throw new Error('editingVersionId is undefined')
      return { automationId, versionId: editingVersionId }
    },
    [
      name,
      description,
      presentation,
      definitions,
      automationId,
      isNewAutomation,
      hasMetadataChanged,
      hasPresentationChanged,
      editingVersionId,
      navigate,
      setLastSavedPresentation,
      setLastSavedName,
      setLastSavedDescription,
      setEditingVersion,
      email
    ]
  )
}
